import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Td, Tr, TableCellProps } from '@chakra-ui/react';
import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useMemo,
  useRef,
} from 'react';

import { finleyAssert } from '@finley-technologies/common';

import { IconButton } from '~/packages/common-frontend/components/IconButton';

type TrWithPanelChildrenProps = {
  isPanelOpen: boolean;
  setIsPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  rowChildrenCount: React.MutableRefObject<number>;
  noPanel: boolean;
};

const TrWithPanelContext = createContext<{
  trWithPanelChildrenProps: TrWithPanelChildrenProps | null;
  setTrWithPanelChildrenProps: React.Dispatch<
    React.SetStateAction<TrWithPanelChildrenProps | null>
  >;
} | null>(null);

const useTrWithPanelContext = () => {
  const context = useContext(TrWithPanelContext);
  if (context === null) {
    throw new Error('TrWithPanelContext must be present');
  }

  return context;
};

type TrWithPanelProviderProps = {
  value: TrWithPanelChildrenProps;
  children: React.ReactNode;
};
const TrWithPanelProvider = ({ value, children }: TrWithPanelProviderProps) => {
  const [
    trWithPanelChildrenProps,
    setTrWithPanelChildrenProps,
  ] = useState<TrWithPanelChildrenProps | null>(value);

  useEffect(() => {
    setTrWithPanelChildrenProps(value);
  }, [value]);

  const context = useMemo(
    () => ({
      trWithPanelChildrenProps,
      setTrWithPanelChildrenProps,
    }),
    [trWithPanelChildrenProps],
  );

  return (
    <TrWithPanelContext.Provider value={context}>
      {children}
    </TrWithPanelContext.Provider>
  );
};

type TrWithPanelProps = {
  children: (input: {
    isPanelOpen: boolean;
    setIsPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
  noPanel?: boolean;
};
export const TrWithPanel = ({ children, noPanel }: TrWithPanelProps) => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const rowChildrenCount = useRef<number>(0);

  return (
    <TrWithPanelProvider
      value={{
        isPanelOpen,
        setIsPanelOpen,
        rowChildrenCount,
        noPanel: noPanel || false,
      }}
    >
      {children({ isPanelOpen, setIsPanelOpen })}
    </TrWithPanelProvider>
  );
};

type TrWithPanelRowProps = { children: React.ReactNode };
export const TrWithPanelRow = ({ children }: TrWithPanelRowProps) => {
  const rowContext = useTrWithPanelContext();
  finleyAssert(rowContext, 'assume context is here');
  const { trWithPanelChildrenProps } = rowContext;
  finleyAssert(trWithPanelChildrenProps, 'TrWithPanelContext context exists');

  const {
    isPanelOpen,
    setIsPanelOpen,
    rowChildrenCount,
    noPanel,
  } = trWithPanelChildrenProps;

  useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    rowChildrenCount.current = React.Children.count(children);
  }, [children, rowChildrenCount]);

  return (
    <Tr>
      {children}
      <Td>
        {!noPanel ? (
          <IconButton
            ariaLabel={isPanelOpen ? 'Collapse details' : 'Expand details'}
            label={isPanelOpen ? 'Collapse details' : 'Expand details'}
            icon={isPanelOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
            onClick={() => {
              setIsPanelOpen((oldIsPanelOpen: boolean) => !oldIsPanelOpen);
            }}
          />
        ) : (
          <Box height="40px" />
        )}
      </Td>
    </Tr>
  );
};

type TrWithPanelPanelProps = {
  isPanelDisabled?: boolean;
  children: React.ReactNode;
  colSpan?: TableCellProps['colSpan'];
};
export const TrWithPanelPanel = ({
  isPanelDisabled = false,
  children,
  colSpan,
}: TrWithPanelPanelProps) => {
  const context = useTrWithPanelContext();
  finleyAssert(context, 'assume context is here');
  const { trWithPanelChildrenProps } = context;
  finleyAssert(trWithPanelChildrenProps, 'assume context is here');

  const { isPanelOpen, rowChildrenCount } = trWithPanelChildrenProps;

  return (
    <Tr
      borderTop="none"
      display={isPanelOpen && !isPanelDisabled ? undefined : 'none'}
    >
      <Td colSpan={colSpan || rowChildrenCount.current + 1} minHeight="35px">
        {children}
      </Td>
    </Tr>
  );
};
